import React, { Fragment, useCallback, useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import { Box, Card } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import Typography from "@mui/material/Typography";
import { equals, map } from "ramda";
import { isBlank, notBlank } from "../../util/ext";
import PanelLoadingIndicator from "../../components/PanelLoadingIndicator";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import { usePendingItems, useUnorganizedItems } from "../creatorServices";
import Button from "@mui/material/Button";
import { capitalize } from "../../util/stringFormat";

const useStyles = makeStyles(theme => ({
  root: {
    width: "100%",
    // minWidth: 415,
    minHeight: 390,
    maxHeight: 390,
    height: "100%",
    paddingLeft: theme.spacing(5),
    paddingRight: theme.spacing(5),
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
    borderRadius: 20,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    display: "flex",
    justifyContent: "flex-start",
    flexDirection: "column",
  },

  h1: {
    fontSize: "3.6rem",
    lineHeight: "3.4rem",
    fontWeight: "bold",
    color: theme.palette.primary.contrastText,
  },

  h4: {
    color: theme.palette.primary.contrastText,
  },

  tableContainer: {
    paddingLeft: 0,
    paddingRight: 0,
  },

  tableRow: {
    borderBottomStyle: "dashed",
    borderBottomColor: theme.palette.primary.contrastText,
    borderBottomWidth: 1,
  },

  tableBody: {
    "& tr:last-child": {
      border: "none",
    },
  },

  tableCellHeader: {
    border: "none",
    fontSize: "1.2rem",
    fontWeight: "normal",
    lineHeight: "1.6rem",
    color: theme.palette.primary.contrastText,
    textTransform: "uppercase",
    paddingLeft: 0,
    paddingRight: 0,
    paddingTop: 20,
    paddingBottom: 10,
  },

  tableCell: {
    border: "none",
    maxWidth: 100,
    color: theme.palette.primary.contrastText,
    textTransform: "none",
    paddingLeft: 0,
    paddingRight: 0,
    paddingTop: 15,
    paddingBottom: 15,
  },

  progressWrapper: {
    flexGrow: 1,
  },

  allButton: {
    fontSize: "1.2rem",
    lineHeight: "1.8rem",
    fontWeight: "bold",
    paddingTop: theme.spacing(2),
    color: theme.palette.primary.contrastText,
  },

  forwardIcon: {
    color: theme.palette.primary.contrastText,

    "& :first-child": {
      fontSize: "1.2rem",
      lineHeight: "1.8rem",
      fontWeight: "bold",
      color: theme.palette.primary.contrastText,
    },
  },

  body1: {
    fontStyle: "italic",
    fontSize: "1.4rem",
    lineHeight: "1.8rem",
    color: theme.palette.primary.contrastText,
  },
}));

const ItemTypes = {
  pending_items: "pending",
  unorganized_items: "unorganized",
};

const ItemTable = ({ items }) => {
  const classes = useStyles();

  return (
    <Box>
      <TableContainer classes={{ root: classes.tableContainer }}>
        <Table aria-label="unorganized table">
          <TableHead>
            <TableRow>
              <TableCell classes={{ root: classes.tableCellHeader }}>
                Organization
              </TableCell>
              <TableCell
                align="right"
                classes={{ root: classes.tableCellHeader }}
              >
                Count
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody classes={{ root: classes.tableBody }}>
            {map(
              data => (
                <TableRow key={data.id} classes={{ root: classes.tableRow }}>
                  <TableCell align="left" classes={{ root: classes.tableCell }}>
                    <Typography noWrap classes={{ body1: classes.body1 }}>
                      {data.name}
                    </Typography>
                  </TableCell>
                  <TableCell
                    align="right"
                    classes={{ root: classes.tableCell }}
                  >
                    {data.count}
                  </TableCell>
                </TableRow>
              ),
              items,
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

const RecentApprovedMediaCard = () => {
  const classes = useStyles();
  const pendingResponse = usePendingItems();
  const unorganizedResponse = useUnorganizedItems();
  const [unorganizedItems, setUnorganizedItems] = useState([]);
  const [pendingItems, setPendingItems] = useState([]);

  const [displayedItemType, setDisplayedItemType] = useState(
    ItemTypes.pending_items,
  );
  useEffect(() => {
    if (pendingResponse.isSuccess) {
      setPendingItems(pendingResponse.data);
    }

    if (unorganizedResponse.isSuccess) {
      setUnorganizedItems(unorganizedResponse.data);
    }
  }, [
    pendingResponse.isSuccess,
    unorganizedResponse.isSuccess,
    pendingResponse.data,
    unorganizedResponse.data,
  ]);

  const handleToggleItemTypes = useCallback(() => {
    setDisplayedItemType(prev =>
      equals(prev, ItemTypes.pending_items)
        ? ItemTypes.unorganized_items
        : ItemTypes.pending_items,
    );
  }, []);

  return (
    <Card classes={{ root: classes.root }}>
      <Grid container justifyContent="space-between" alignItems="center">
        <Grid item>
          <Typography variant="h4" component="div" classes={{ h4: classes.h4 }}>
            {`${capitalize(displayedItemType)} Items`}
          </Typography>
        </Grid>
        <Grid item>
          <Button
            style={{ color: "white" }}
            variant={"text"}
            onClick={handleToggleItemTypes}
          >
            {equals(displayedItemType, ItemTypes.pending_items)
              ? "Show Unorganized"
              : "Show Pending"}
          </Button>
        </Grid>
      </Grid>
      {equals(displayedItemType, ItemTypes.pending_items) && (
        <Fragment>
          {pendingResponse.isError && (
            <Box padding={5} color="error" fontSize="1.4rem" fontStyle="italic">
              Error loading pending files.
            </Box>
          )}

          {pendingResponse.isLoading &&
            equals(displayedItemType, ItemTypes.pending_items) && (
              <PanelLoadingIndicator color="secondary" />
            )}

          {pendingResponse.isSuccess && isBlank(pendingItems) && (
            <Box pl={0} pr={0} pt={5} fontSize="1.4rem" fontStyle="italic">
              {`There are no Organizations with pending files.`}
            </Box>
          )}

          {pendingResponse.isSuccess && notBlank(pendingItems) && (
            <ItemTable items={pendingItems} />
          )}
        </Fragment>
      )}
      {equals(displayedItemType, ItemTypes.unorganized_items) && (
        <Fragment>
          {unorganizedResponse.isError && (
            <Box padding={5} color="error" fontSize="1.4rem" fontStyle="italic">
              Error loading Unorganized files.
            </Box>
          )}

          {unorganizedResponse.isLoading && (
            <PanelLoadingIndicator color="secondary" />
          )}

          {unorganizedResponse.isSuccess && isBlank(unorganizedItems) && (
            <Box pl={0} pr={0} pt={5} fontSize="1.4rem" fontStyle="italic">
              {`There are no Organizations with unorganized files.`}
            </Box>
          )}

          {unorganizedResponse.isSuccess && notBlank(unorganizedItems) && (
            <ItemTable items={unorganizedItems} />
          )}
        </Fragment>
      )}
    </Card>
  );
};

export default RecentApprovedMediaCard;
